import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useViewport } from '../../hooks/useViewport';
import { unifiedApiService } from '../../services/unified';
import { useWebSocket } from '../../hooks/useWebSocket';
import type { EnhancedConversationMessage } from '../../services/unified/types';

// Authenticated Image Component - fetches image with auth token and displays as blob
function AuthenticatedImage({ 
  filename, 
  alt, 
  className, 
  onClick 
}: { 
  filename: string; 
  alt: string; 
  className?: string; 
  onClick?: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const blob = await unifiedApiService.messaging.getAttachment(filename);
        if (mounted) {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
      } catch (err) {
        console.error('Failed to load image:', err);
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadImage();
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [filename]);

  if (loading) {
    return (
      <div className={`${className} bg-gray-200 animate-pulse flex items-center justify-center`}>
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center text-gray-400`}>
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    );
  }

  return <img src={blobUrl} alt={alt} className={className} onClick={onClick} />;
}

interface Conversation {
  user_id: string;
  user_name: string;
  user_email: string;
  user_avatar?: string;
  last_message: string;
  last_message_at: string;
  unread_count: number;
}

// Using EnhancedConversationMessage from types

interface MessageTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: 'application' | 'interview' | 'rejection' | 'general';
}

const messageTemplates: MessageTemplate[] = [
  {
    id: 'app-received',
    name: 'Application Received',
    subject: 'Application Received - {{jobTitle}}',
    category: 'application',
    content: `Dear {{candidateName}},

Thank you for your interest in the {{jobTitle}} position at {{companyName}}. We have received your application and are currently reviewing it.

We will get back to you within 3-5 business days with an update on your application status.

Best regards,
The Hiring Team`
  },
  {
    id: 'interview-invite',
    name: 'Interview Invitation',
    subject: 'Interview Invitation - {{jobTitle}}',
    category: 'interview',
    content: `Dear {{candidateName}},

We are pleased to invite you for an interview for the {{jobTitle}} position. Based on your application and resume, we believe you could be a great fit for our team.

Please reply to this message with your availability for the following time slots:
- {{timeSlot1}}
- {{timeSlot2}}
- {{timeSlot3}}

The interview will be conducted {{interviewType}} and is expected to last approximately {{duration}}.

We look forward to speaking with you!

Best regards,
The Hiring Team`
  },
  {
    id: 'application-rejected',
    name: 'Application Declined',
    subject: 'Update on Your Application - {{jobTitle}}',
    category: 'rejection',
    content: `Dear {{candidateName}},

Thank you for your interest in the {{jobTitle}} position at {{companyName}} and for taking the time to apply.

After careful consideration, we have decided to move forward with other candidates whose experience more closely matches our current needs.

We encourage you to apply for future positions that match your skills and experience. We will keep your resume on file for future opportunities.

Thank you again for your interest in {{companyName}}.

Best regards,
The Hiring Team`
  }
];

interface BulkMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedConversations: string[];
  onMessageSent: () => void;
}

function BulkMessageModal({ isOpen, onClose, selectedConversations, onMessageSent }: BulkMessageModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTemplateSelect = (templateId: string) => {
    const template = messageTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setSubject(template.subject);
      setMessage(template.content);
    } else {
      setSelectedTemplate('');
      setSubject('');
      setMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual bulk message API
      await new Promise(resolve => setTimeout(resolve, 1000)); // Mock API call
      onMessageSent();
      onClose();
      setSelectedTemplate('');
      setSubject('');
      setMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send messages');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Send Bulk Message</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Sending message to {selectedConversations.length} candidate(s).
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Template (Optional)
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a template or write custom message</option>
                {messageTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Message subject"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={8}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Type your message here..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                You can use variables like {`{{candidateName}}`}, {`{{jobTitle}}`}, {`{{companyName}}`} in templates.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !message.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Messages'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export function AdminMessagesPage() {
  const { isMobile } = useViewport();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<EnhancedConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConversations, setSelectedConversations] = useState<string[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // WebSocket connection
  const {
    isConnected,
    connectionState,
    sendMessage: sendWebSocketMessage,
    uploadFile: uploadWebSocketFile,
  } = useWebSocket({
    autoConnect: true,
    onMessage: handleWebSocketMessage,
    onError: (err) => {
      console.error('WebSocket error:', err);
    },
  });

  // Handle incoming WebSocket messages
  function handleWebSocketMessage(message: any) {
    console.log('Admin received WebSocket message:', message);
    console.log('Selected conversation:', selectedConversation?.user_id);
    console.log('Message user_id:', message.user_id);
    
    // Add message to the list if it's for the selected conversation
    if (selectedConversation && message.user_id === selectedConversation.user_id) {
      console.log('Adding message to list:', message);
      setMessages((prev) => {
        // Avoid duplicates
        if (prev.some((m) => m.id === message.id)) {
          console.log('Duplicate message, skipping');
          return prev;
        }
        console.log('Adding new message to state');
        return [...prev, message];
      });

      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      console.log('Message not for selected conversation, updating conversation list only');
    }

    // Update conversation list
    setConversations((prev) =>
      prev.map((conv) =>
        conv.user_id === message.user_id
          ? {
              ...conv,
              last_message: message.message,
              last_message_at: message.created_at,
              unread_count: message.sender === 'user' ? conv.unread_count + 1 : conv.unread_count,
            }
          : conv
      )
    );
  }

  // Fetch conversations on mount (no polling - WebSocket handles updates)
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const conversationsData = await unifiedApiService.admin.getAllConversations();
        setConversations(conversationsData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch conversations:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
    // No polling needed - WebSocket handles real-time updates!
  }, []);

  // Fetch messages when conversation is selected (no polling - WebSocket handles updates)
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    const fetchMessages = async () => {
      try {
        const messagesData = await unifiedApiService.messaging.getAdminConversations(
          selectedConversation.user_id
        );
        console.log('Admin messages loaded:', messagesData);
        setMessages(messagesData as any);
        setError(null);
        
        // Mark conversation as read
        if (selectedConversation.unread_count > 0) {
          try {
            await unifiedApiService.messaging.markAdminConversationRead(selectedConversation.user_id);
            // Update local state to reflect read status
            setConversations(prev => 
              prev.map(conv => 
                conv.user_id === selectedConversation.user_id 
                  ? { ...conv, unread_count: 0 }
                  : conv
              )
            );
          } catch (markErr) {
            console.error('Failed to mark conversation as read:', markErr);
          }
        }
        
        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } catch (err) {
        console.error('Failed to fetch messages:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch messages');
      }
    };

    fetchMessages();
    // No polling needed - WebSocket handles real-time updates!
  }, [selectedConversation]);

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversations(prev => 
      prev.includes(conversationId) 
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId]
    );
  };

  const handleSelectAll = () => {
    const filteredConversations = getFilteredConversations();
    if (selectedConversations.length === filteredConversations.length) {
      setSelectedConversations([]);
    } else {
      setSelectedConversations(filteredConversations.map(conv => conv.user_id));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validate file sizes (max 10MB per file)
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConversation) return;

    setSendingMessage(true);
    setError(null);
    
    const messageContent = newMessage;
    const messageAttachments = [...attachments];
    
    // Clear form immediately for better UX
    setNewMessage('');
    setAttachments([]);
    
    try {
      if (messageAttachments.length > 0 && isConnected) {
        // Use WebSocket for file attachments
        for (const file of messageAttachments) {
          const reader = new FileReader();
          reader.onload = () => {
            const base64Data = (reader.result as string).split(',')[1];
            uploadWebSocketFile({
              filename: file.name,
              mime_type: file.type,
              data: base64Data,
              conversation_id: selectedConversation.user_id,
            });
          };
          reader.readAsDataURL(file);
        }
        
        // Also send text message if provided
        if (messageContent.trim()) {
          sendWebSocketMessage({
            content: messageContent,
            conversation_id: selectedConversation.user_id,
          });
        }
        
        // Refresh messages after a short delay to get the uploaded files
        setTimeout(async () => {
          const messagesData = await unifiedApiService.messaging.getAdminConversations(
            selectedConversation.user_id
          );
          setMessages(messagesData as any);
        }, 1000);
      } else if (isConnected) {
        // Use WebSocket for text messages (instant delivery)
        sendWebSocketMessage({
          content: messageContent,
          conversation_id: selectedConversation.user_id,
        });
        
        // Optimistic UI update - add message immediately
        const optimisticMessage: EnhancedConversationMessage = {
          id: `temp-${Date.now()}`,
          user_id: selectedConversation.user_id,
          sender: 'admin',
          message: messageContent,
          attachments: [],
          is_read: false,
          created_at: new Date().toISOString(),
        };
        
        setMessages((prev) => [...prev, optimisticMessage]);
      } else {
        // Fallback to REST API if WebSocket not connected (text only)
        await unifiedApiService.messaging.sendAdminMessage(
          selectedConversation.user_id,
          { message: messageContent }
        );
        // Refresh messages after sending
        const messagesData = await unifiedApiService.messaging.getAdminConversations(
          selectedConversation.user_id
        );
        setMessages(messagesData as any);
      }
      
      setUploadProgress(0);
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      // Restore form on error
      setNewMessage(messageContent);
      setAttachments(messageAttachments);
    } finally {
      setSendingMessage(false);
      setUploadProgress(0);
    }
  };

  const getFilteredConversations = () => {
    return conversations.filter(conv => {
      const matchesSearch = conv.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           conv.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           conv.last_message.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  };

  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string; isBlob: boolean } | null>(null);

  // Handle preview with authenticated fetch
  const handlePreviewAttachment = useCallback(async (filename: string, originalFilename: string) => {
    try {
      const blob = await unifiedApiService.messaging.getAttachment(filename);
      const url = URL.createObjectURL(blob);
      setPreviewImage({ url, filename: originalFilename, isBlob: true });
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError('Failed to load preview');
    }
  }, []);

  // Close preview and cleanup blob URL
  const closePreview = useCallback(() => {
    if (previewImage?.isBlob) {
      URL.revokeObjectURL(previewImage.url);
    }
    setPreviewImage(null);
  }, [previewImage]);

  const isImageFile = (filename: string) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '');
  };

  const isPdfFile = (filename: string) => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  const handleDownloadAttachment = async (filename: string, originalFilename: string) => {
    try {
      const blob = await unifiedApiService.messaging.getAttachment(filename);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      setError('Failed to download attachment');
    }
  };

  const filteredConversations = getFilteredConversations();

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="lg:col-span-2">
                <div className="h-96 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-8">
          <div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Messages</h1>
              {/* WebSocket Connection Status */}
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  connectionState === 'connected' ? 'bg-green-500' :
                  connectionState === 'connecting' || connectionState === 'reconnecting' ? 'bg-yellow-500 animate-pulse' :
                  'bg-red-500'
                }`} />
                <span className="text-xs text-gray-500">
                  {connectionState === 'connected' ? 'Live' :
                   connectionState === 'connecting' ? 'Connecting...' :
                   connectionState === 'reconnecting' ? 'Reconnecting...' :
                   'Offline'}
                </span>
              </div>
            </div>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Manage candidate communications and send bulk messages.
            </p>
          </div>
          {selectedConversations.length > 0 && (
            <button
              onClick={() => setShowBulkModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium min-h-[44px]"
            >
              Send Bulk ({selectedConversations.length})
            </button>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Conversations List - Hidden on mobile when conversation is selected */}
          <div className={`bg-white shadow-lg rounded-xl overflow-hidden ${isMobile && selectedConversation ? 'hidden' : ''}`}>
            {/* Search Header */}
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                />
              </div>
              <div className="flex items-center mt-3">
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedConversations.length === filteredConversations.length && filteredConversations.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">Select All ({filteredConversations.length})</span>
                </label>
              </div>
            </div>

            {/* Conversations */}
            <div className="max-h-[500px] overflow-y-auto">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.user_id}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-all duration-150 ${
                    selectedConversation?.user_id === conversation.user_id 
                      ? 'bg-primary-50 border-l-4 border-l-primary-500' 
                      : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                  }`}
                  onClick={() => setSelectedConversation(conversation)}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedConversations.includes(conversation.user_id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectConversation(conversation.user_id);
                      }}
                      className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    {conversation.user_avatar ? (
                      <img
                        src={conversation.user_avatar}
                        alt={conversation.user_name}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        onError={(e) => {
                          // Fallback to initials on error
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0 ${conversation.user_avatar ? 'hidden' : ''}`}>
                      <span className="text-white font-semibold text-sm">
                        {conversation.user_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {conversation.user_name}
                        </h4>
                        {conversation.unread_count > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                            {conversation.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{conversation.user_email}</p>
                      <p className="text-sm text-gray-600 mt-1 truncate">{conversation.last_message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(conversation.last_message_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredConversations.length === 0 && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No conversations found</p>
              </div>
            )}
          </div>

          {/* Message Thread - Full width on mobile when conversation selected */}
          <div className={`lg:col-span-2 bg-white shadow-lg rounded-xl overflow-hidden flex flex-col ${isMobile && !selectedConversation ? 'hidden' : ''}`}>
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-white shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      {/* Back button for mobile */}
                      {isMobile && (
                        <button
                          onClick={() => setSelectedConversation(null)}
                          className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                      )}
                      {selectedConversation.user_avatar ? (
                        <img
                          src={selectedConversation.user_avatar}
                          alt={selectedConversation.user_name}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center ${selectedConversation.user_avatar ? 'hidden' : ''}`}>
                        <span className="text-white font-semibold text-sm sm:text-base">
                          {selectedConversation.user_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                          {selectedConversation.user_name}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{selectedConversation.user_email}</p>
                      </div>
                    </div>
                    <Link
                      to={`/admin/candidates/${selectedConversation.user_id}`}
                      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      View Profile
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </Link>
                  </div>
                </div>

                {/* Messages */}
                <div className="p-4 h-96 overflow-y-auto bg-gray-50">
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isAdmin = message.sender.toLowerCase().includes('admin');
                      return (
                        <div
                          key={message.id}
                          className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} gap-2`}
                        >
                          {!isAdmin && (
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                            </div>
                          )}
                          <div className={`max-w-xs lg:max-w-md ${isAdmin ? 'order-1' : ''}`}>
                            <div
                              className={`px-4 py-3 rounded-2xl shadow-sm ${
                                isAdmin
                                  ? 'bg-primary-600 text-white rounded-br-md'
                                  : 'bg-white text-gray-900 rounded-bl-md border border-gray-100'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.message}</p>
                              
                              {/* Attachments - WhatsApp Style */}
                              {message.attachments && message.attachments.length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {message.attachments.map((attachment, idx) => {
                                    const isImage = isImageFile(attachment.original_filename);
                                    const isPdf = isPdfFile(attachment.original_filename);
                                    
                                    if (isImage) {
                                      return (
                                        <div key={idx} className="relative group rounded-lg overflow-hidden">
                                          <AuthenticatedImage
                                            filename={attachment.filename}
                                            alt={attachment.original_filename}
                                            className="max-w-[200px] max-h-[200px] object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => handlePreviewAttachment(attachment.filename, attachment.original_filename)}
                                          />
                                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 pointer-events-auto">
                                              <button
                                                onClick={(e) => { e.stopPropagation(); handlePreviewAttachment(attachment.filename, attachment.original_filename); }}
                                                className="p-2 bg-white/90 text-gray-700 rounded-full shadow-lg hover:bg-white"
                                                title="Preview"
                                              >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                              </button>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); handleDownloadAttachment(attachment.filename, attachment.original_filename); }}
                                                className="p-2 bg-white/90 text-gray-700 rounded-full shadow-lg hover:bg-white"
                                                title="Download"
                                              >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    // PDF and other files - card style
                                    return (
                                      <div
                                        key={idx}
                                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                                          isAdmin 
                                            ? 'bg-primary-500/80 hover:bg-primary-400/80' 
                                            : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
                                        }`}
                                        onClick={() => isPdf ? handlePreviewAttachment(attachment.filename, attachment.original_filename) : handleDownloadAttachment(attachment.filename, attachment.original_filename)}
                                      >
                                        <div className={`p-2.5 rounded-xl ${isAdmin ? 'bg-primary-400/50' : 'bg-white shadow-sm'}`}>
                                          {isPdf ? (
                                            <svg className={`w-6 h-6 ${isAdmin ? 'text-white' : 'text-red-500'}`} fill="currentColor" viewBox="0 0 24 24">
                                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13h1c.55 0 1 .45 1 1v3c0 .55-.45 1-1 1h-1c-.55 0-1-.45-1-1v-3c0-.55.45-1 1-1zm0 1v3h1v-3h-1zm3 0h1.5v.5h-1v.5h.5c.55 0 1 .45 1 1v.5c0 .55-.45 1-1 1H11.5v-.5h1v-.5h-.5c-.55 0-1-.45-1-1v-.5c0-.55.45-1 1-1zm3 0h1v2h.5v-2h.5v2.5c0 .28-.22.5-.5.5h-1c-.28 0-.5-.22-.5-.5V14z"/>
                                            </svg>
                                          ) : (
                                            <svg className={`w-6 h-6 ${isAdmin ? 'text-white' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium truncate ${isAdmin ? 'text-white' : 'text-gray-900'}`}>
                                            {attachment.original_filename}
                                          </p>
                                          <p className={`text-xs ${isAdmin ? 'text-primary-200' : 'text-gray-500'}`}>
                                            {(attachment.file_size / 1024).toFixed(1)} KB • {isPdf ? 'Click to preview' : 'Click to download'}
                                          </p>
                                        </div>
                                        <svg className={`w-5 h-5 flex-shrink-0 ${isAdmin ? 'text-primary-200' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            
                            {/* Timestamp */}
                            <div className={`mt-1 flex items-center gap-1.5 text-xs text-gray-400 ${
                              isAdmin ? 'justify-end' : 'justify-start'
                            }`}>
                              <span>{message.created_at ? new Date(message.created_at).toLocaleString() : 'Just now'}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Message Input */}
                <div className="px-4 py-3 border-t border-gray-200 bg-white">
                  <form onSubmit={handleSendMessage}>
                    {/* Attachments Preview */}
                    {attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {attachments.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs group hover:border-primary-300 transition-colors"
                          >
                            {file.type.startsWith('image/') ? (
                              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            ) : file.type === 'application/pdf' ? (
                              <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                            )}
                            <span className="text-gray-700 truncate max-w-[100px]">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload Progress */}
                    {sendingMessage && uploadProgress > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-1 mb-2">
                        <div
                          className="bg-primary-600 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}
                    
                    {/* Input Row: Input | Attachment | Send */}
                    <div className="flex items-center gap-2">
                      {/* Text Input */}
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (newMessage.trim() || attachments.length > 0) {
                              handleSendMessage(e);
                            }
                          }
                        }}
                        placeholder="Type your message..."
                        rows={1}
                        disabled={sendingMessage}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors disabled:opacity-50 text-sm"
                        style={{ minHeight: '40px', maxHeight: '100px' }}
                      />
                      
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                      />
                      
                      {/* Attachment Button */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sendingMessage}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        title="Attach file"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                      </button>
                      
                      {/* Send Button */}
                      <button
                        type="submit"
                        disabled={sendingMessage || (!newMessage.trim() && attachments.length === 0)}
                        className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center flex-shrink-0"
                        title="Send message"
                      >
                        {sendingMessage ? (
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        )}
                      </button>
                    </div>
                    
                    {/* Keyboard Shortcut Hint */}
                    <p className="text-[10px] text-gray-400 mt-1.5 text-center">
                      Press Enter to send, Shift+Enter for new line
                    </p>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No conversation selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a conversation from the list to start messaging.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <BulkMessageModal
          isOpen={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          selectedConversations={selectedConversations}
          onMessageSent={() => {
            setSelectedConversations([]);
            // Refresh conversations
          }}
        />

        {/* File Preview Modal */}
        {previewImage && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={closePreview}
          >
            <div className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-medium text-gray-900 truncate">
                  {previewImage.filename}
                </h3>
                <div className="flex items-center gap-2">
                  <a
                    href={previewImage.url}
                    download={previewImage.filename}
                    className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Download"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </a>
                  <button
                    onClick={closePreview}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="p-4 overflow-auto max-h-[calc(90vh-60px)] bg-gray-100 flex items-center justify-center">
                {previewImage.filename.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={previewImage.url}
                    className="w-full h-[70vh] rounded-lg border border-gray-200 bg-white"
                    title="PDF Preview"
                  />
                ) : (
                  <img
                    src={previewImage.url}
                    alt={previewImage.filename}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}